import { Position, MultipleRadioButton } from '@consigli/facade';
import { EMAIL_PATTERN, useMyUser } from '@consigli/hooks';
import { OrganizationRole, ProjectRole } from '@consigli/types';
import { useCallback, useEffect, useMemo } from 'react';
import {
  Control,
  Controller,
  FieldErrors,
  FieldValues,
  UseFormGetValues,
  UseFormRegister,
  UseFormSetValue,
  UseFormWatch,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { IoIosInformationCircle } from 'react-icons/io';

import { MultiEmailInput } from './multi-email-input';

interface InviteFieldsProps {
  register: UseFormRegister<FieldValues>;
  control: Control<FieldValues>;
  getValues: UseFormGetValues<FieldValues>;
  setValue: UseFormSetValue<FieldValues>;
  errors: FieldErrors<FieldValues>;
  watch: UseFormWatch<FieldValues>;
}

export const InviteFields = (props: InviteFieldsProps) => {
  const { register, control, getValues, setValue, errors, watch } = props;
  const {
    user: { isOrgAdmin, organizationId, projectAccess },
  } = useMyUser();

  const { t } = useTranslation();
  const organizationRole = watch('organizationRole');
  const organizationOptions = useMemo(
    () => [
      {
        label: t('invite-user.admin'),
        value: OrganizationRole.ADMIN,
      },
      {
        label: t('invite-user.user'),
        value: OrganizationRole.USER,
      },
    ],
    [t],
  );

  const showAdminInfo = useMemo(() => {
    return organizationRole === undefined || organizationRole === '';
  }, [organizationRole]);

  useEffect(() => {
    if (!isOrgAdmin && projectAccess.some((access) => access.role === ProjectRole.ADMIN)) {
      setValue('organizationRole', OrganizationRole.USER);
    }
  }, [isOrgAdmin, setValue, organizationId, projectAccess]);

  const validateRecipientEmail = useCallback(
    (email: string) => {
      if (!EMAIL_PATTERN.test(email)) return `${t('invite-user.valid-email')}*`;
      if (!email.toLowerCase().endsWith('@aecom.com')) {
        return `${t('invite-user.aecom-email-required')}*`;
      }
      return undefined;
    },
    [t],
  );

  const recipientsError = errors.recipients?.message as string | undefined;

  return (
    <div>
      <div className="mt-4 mb-6">
        <Controller
          name="recipients"
          control={control}
          defaultValue={[]}
          rules={{
            validate: (value: string[]) =>
              (Array.isArray(value) && value.length > 0) ||
              `${t('invite-user.recipients-required')}*`,
          }}
          render={({ field }) => (
            <MultiEmailInput
              label={t('invite-user.recipients-label')}
              placeholder={t('invite-user.enter-emails-placeholder')}
              value={field.value ?? []}
              onChange={field.onChange}
              validateEmail={validateRecipientEmail}
              formError={recipientsError}
            />
          )}
        />
      </div>
      {isOrgAdmin && (
        <>
          <MultipleRadioButton
            data={organizationOptions}
            label={t('invite-user.role')}
            id="organizationRole"
            control={control}
            register={register}
            errors={errors}
            getValues={getValues}
            setValue={setValue}
            labelPosition={Position.LEFT}
            errorMessage={`${t('user_administration.id-required')}*`}
          />
          {showAdminInfo && (
            <div className="flex mt-2 opacity-50">
              <IoIosInformationCircle />
              <p className="text-xs ml-1">
                <b>{t('invite-user.role-admin')}</b> {t('invite-user.role-admin-description')}
              </p>
            </div>
          )}
        </>
      )}
    </div>
  );
};
